const INTERACTIVE_LAYER_ID_PATTERN = /^layer(?:-(?:point|line|fill))?-(\d+)$/

export function getCommunityMapLayerSourceId(layerId) {
  return `layers-source-${layerId}`
}

export function getCommunityMapLayerInteractiveIds(layer) {
  const layerId = layer.id
  if (layer.mapAttribute && layer.palette) {
    return [`layer-${layerId}`, `layer-fill-${layerId}`]
  }
  return [`layer-point-${layerId}`, `layer-line-${layerId}`, `layer-fill-${layerId}`]
}

export function parseCommunityMapLayerIdFromInteractiveLayerId(interactiveLayerId) {
  const match = interactiveLayerId.match(INTERACTIVE_LAYER_ID_PATTERN)
  if (!match) return null
  const parsed = Number(match[1])
  return Number.isFinite(parsed) ? parsed : null
}

export function formatCommunityMapLayerPropertyValue(value) {
  if (value == null || value === '') return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export function buildCommunityMapLayerPropertyRows(properties) {
  if (!properties || typeof properties !== 'object') return []
  return Object.entries(properties)
    .filter(([key]) => key !== '' && !key.startsWith('_'))
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([key, value]) => ({
      key,
      value: formatCommunityMapLayerPropertyValue(value),
    }))
}

function collectGeoJsonCoordinates(node, coords) {
  if (!Array.isArray(node)) return
  if (typeof node[0] === 'number' && typeof node[1] === 'number') {
    coords.push(node)
    return
  }
  node.forEach((child) => collectGeoJsonCoordinates(child, coords))
}

export function getCommunityMapLayerCenter(features) {
  if (!Array.isArray(features) || features.length === 0) return null

  const coords = []
  features.forEach((feature) => {
    collectGeoJsonCoordinates(feature?.geometry?.coordinates, coords)
  })
  if (coords.length === 0) return null

  const totals = coords.reduce(
    (acc, [lon, lat]) => ({ lon: acc.lon + lon, lat: acc.lat + lat }),
    { lon: 0, lat: 0 },
  )
  return [totals.lon / coords.length, totals.lat / coords.length]
}

export function getCommunityMapLayerBounds(features) {
  if (!Array.isArray(features) || features.length === 0) return null

  const coords = []
  features.forEach((feature) => {
    collectGeoJsonCoordinates(feature?.geometry?.coordinates, coords)
  })
  if (coords.length === 0) return null

  let west = coords[0][0]
  let east = coords[0][0]
  let south = coords[0][1]
  let north = coords[0][1]

  coords.forEach(([lon, lat]) => {
    west = Math.min(west, lon)
    east = Math.max(east, lon)
    south = Math.min(south, lat)
    north = Math.max(north, lat)
  })

  if (west === east && south === north) {
    const pad = 0.01
    return [[west - pad, south - pad], [east + pad, north + pad]]
  }

  return [[west, south], [east, north]]
}

export function extractCommunityMapLayerFeatures(fileContent) {
  if (!fileContent) return []
  if (Array.isArray(fileContent.features)) return fileContent.features
  if (Array.isArray(fileContent)) return fileContent
  return []
}

export const COMMUNITY_MAP_LAYER_ATTRIBUTE_WARNING_CODES = {
  MISSING_PROPERTY: 'missing_property',
  NOT_NUMERIC: 'not_numeric',
  PARTIAL_MISSING: 'partial_missing',
}

export function getCommunityMapLayerAttributeWarning(features, mapAttribute, palette) {
  if (!mapAttribute || !palette) return null
  if (!Array.isArray(features) || features.length === 0) return null

  let numericCount = 0
  let presentButInvalidCount = 0

  features.forEach((feature) => {
    const raw = feature?.properties?.[mapAttribute]
    if (raw === undefined || raw === null || raw === '') return
    const numeric = Number.parseFloat(String(raw))
    if (Number.isFinite(numeric)) {
      numericCount += 1
      return
    }
    presentButInvalidCount += 1
  })

  if (numericCount === 0) {
    if (presentButInvalidCount > 0) {
      return {
        code: COMMUNITY_MAP_LAYER_ATTRIBUTE_WARNING_CODES.NOT_NUMERIC,
        mapAttribute,
      }
    }
    return {
      code: COMMUNITY_MAP_LAYER_ATTRIBUTE_WARNING_CODES.MISSING_PROPERTY,
      mapAttribute,
    }
  }

  if (numericCount < features.length) {
    return {
      code: COMMUNITY_MAP_LAYER_ATTRIBUTE_WARNING_CODES.PARTIAL_MISSING,
      mapAttribute,
      validCount: numericCount,
      totalCount: features.length,
    }
  }

  return null
}

export function resolveCommunityMapLayerAttributeWarning(layer) {
  if (!layer?.mapAttribute || !layer?.palette) return null
  const features = extractCommunityMapLayerFeatures(layer.fileContent)
  if (features.length === 0) return null
  return getCommunityMapLayerAttributeWarning(features, layer.mapAttribute, layer.palette)
}

export function hasValidCommunityMapLayerAttribute(features, mapAttribute, palette) {
  return getCommunityMapLayerAttributeWarning(features, mapAttribute, palette) === null
}

export function getCommunityMapLayerFeaturesFromMapSource(mapInstance, layerId) {
  const map = resolveMapLibreMapInstance(mapInstance)
  if (!map?.querySourceFeatures) return []

  const sourceId = getCommunityMapLayerSourceId(layerId)
  try {
    return map.querySourceFeatures(sourceId).map((feature) => ({
      type: 'Feature',
      geometry: feature.geometry,
      properties: feature.properties ?? {},
    }))
  } catch {
    return []
  }
}

export function resolveCommunityMapLayerFeatures(layer, mapInstance) {
  const fileFeatures = extractCommunityMapLayerFeatures(layer?.fileContent)
  if (fileFeatures.length > 0) return fileFeatures
  if (!layer?.id) return []
  return getCommunityMapLayerFeaturesFromMapSource(mapInstance, layer.id)
}

export function resolveMapLibreMapInstance(mapRefOrInstance) {
  if (!mapRefOrInstance) return null
  if (typeof mapRefOrInstance.fitBounds === 'function') {
    return mapRefOrInstance
  }
  if (typeof mapRefOrInstance.getMap === 'function') {
    const rawMap = mapRefOrInstance.getMap()
    if (rawMap?.fitBounds) return rawMap
  }
  if (mapRefOrInstance.current) {
    return resolveMapLibreMapInstance(mapRefOrInstance.current)
  }
  return null
}

export function fitMapToCommunityMapLayerBounds(mapInstance, features) {
  const bounds = getCommunityMapLayerBounds(features)
  const map = resolveMapLibreMapInstance(mapInstance)
  if (!bounds || !map?.fitBounds) return false

  map.fitBounds(bounds, {
    padding: 48,
    maxZoom: 14,
    duration: 900,
  })
  return true
}

export function fitMapToCommunityMapLayer(mapRef, features) {
  return fitMapToCommunityMapLayerBounds(mapRef, features)
}
